import React, { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";

import bg from "../../assets/images/bg.png";

import ContactUs from "../contacts/ContactUs";

/* -------------------------------- Main --------------------------------- */
function Main() {
  const containerRef = useRef(null);

  // Parallax bg
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end end"],
  });
  const [maxShift, setMaxShift] = useState(0);
  useEffect(() => {
    const set = () => setMaxShift(Math.round(window.innerHeight * 0.18));
    set();
    window.addEventListener("resize", set);
    return () => window.removeEventListener("resize", set);
  }, []);
  const bgY = useTransform(scrollYProgress, [0, 1], [0, -maxShift]);

  useEffect(() => {
    document.documentElement.style.scrollBehavior = "smooth";
  }, []);

  return (
    <div ref={containerRef} className="pt-16 font-inter text-text-body relative isolate">
      {/* Parallax Background — clipped to hero only */}
      <div className="absolute top-0 left-0 right-0 h-[88vh] overflow-hidden z-0 pointer-events-none">
        <motion.div
          style={{ y: bgY }}
          className="absolute inset-0 will-change-transform"
        >
          <img
            src={bg}
            alt=""
            aria-hidden="true"
            className="absolute inset-0 w-full h-full object-cover object-bottom"
          />
          {/* Subtle warm color wash for atmosphere */}
          <div className="absolute inset-0 bg-gradient-to-br from-amber-100/15 via-transparent to-sky-50/15" />
        </motion.div>
        {/* Smooth fade to base color at bottom */}
        <div className="absolute bottom-0 left-0 right-0 h-40 bg-gradient-to-b from-transparent via-bg-base/85 to-bg-base" />
      </div>

      {/* Content */}
      <main className="relative z-10 space-y-0">
        {/* HOME */}
        <div className="px-5 sm:px-8 md:px-10">
          <Section id="home" />
        </div>

        {/* SOCIAL PROOF STRIP */}
        <SocialProofStrip />

        {/* ABOUT (dark band) */}
        <AboutSimple />

        {/* SOLAR CALCULATOR */}
        <SolarCalculator />

        {/* CONTACTS (kept on landing) */}
        <ContactUs />
      </main>
    </div>
  );
}

/* ------------------------------- Sections ------------------------------ */
function Section({ id }) {
  const pad = id === "home" ? "py-12 md:py-16" : "py-12 md:py-14";

  return (
    <section id={id} className="scroll-mt-16">
      <motion.div
        className={`relative ${pad}`}
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: "easeOut" }}
        viewport={{ once: true, margin: "-20% 0px -20% 0px" }}
      >
        {id === "home" && (
          <>
            <motion.div
              className="relative rounded-3xl border border-white/40 shadow-[0_20px_60px_-15px_rgba(15,23,42,0.25),0_8px_24px_-12px_rgba(15,23,42,0.15)] overflow-hidden bg-white/25 backdrop-blur-md max-w-5xl mx-auto"
            >
              {/* Accent ribbon */}
              <div className="absolute left-0 right-0 top-0 h-[3px] bg-gradient-to-r from-accent-green via-accent-sun to-accent-sky" />

              {/* content */}
              <div className="relative z-[1] max-w-4xl mx-auto text-center space-y-6 py-14 px-6 md:py-16 md:px-12">
                <motion.div
                  className="inline-flex items-center gap-2.5 rounded-full px-4 py-1.5 ring-1 ring-accent-green/20 bg-accent-green/5"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.15, duration: 0.6 }}
                >
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-accent-green opacity-75" />
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-accent-green" />
                  </span>
                  <span className="uppercase tracking-[0.18em] text-[11px] text-accent-green font-semibold">
                    Clean Energy for Filipino Homes
                  </span>
                </motion.div>

                <motion.h1
                  className="font-display text-[2.75rem] sm:text-5xl md:text-6xl lg:text-7xl font-bold text-text-primary leading-[1.05] tracking-tight"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.25, duration: 0.8 }}
                >
                  Stop Paying More.<br />
                  <span className="bg-gradient-to-r from-accent-sun via-orange-500 to-amber-500 bg-clip-text text-transparent">
                    Start Owning Your Power.
                  </span>
                </motion.h1>

                <motion.p
                  className="text-lg md:text-xl text-text-body max-w-2xl mx-auto leading-relaxed font-normal"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.35, duration: 0.8 }}
                >
                  Solar systems engineered for Filipino homes — grid-tie, hybrid,
                  professionally installed and fully warranted.
                </motion.p>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.45, duration: 0.8 }}
                  className="flex flex-col sm:flex-row items-center justify-center gap-3 pt-4"
                >
                  <a
                    href="#calculator"
                    className="group inline-flex items-center gap-2 px-7 py-3.5 rounded-xl bg-text-primary hover:bg-slate-800 text-white font-semibold text-[15px] transition-all transform hover:translate-y-[-2px] hover:shadow-xl shadow-md"
                  >
                    <span>Compute My Savings</span>
                    <span className="transition-transform group-hover:translate-x-1">→</span>
                  </a>
                  <a
                    href="/products"
                    className="inline-flex items-center gap-2 px-7 py-3.5 rounded-xl bg-white/80 backdrop-blur border border-border-light text-text-primary hover:border-text-primary hover:bg-white font-semibold text-[15px] transition"
                  >
                    <span>Browse Systems</span>
                  </a>
                </motion.div>

                {/* Trust strip */}
                <motion.div
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  transition={{ delay: 0.55, duration: 0.8 }}
                  className="mt-10 pt-8 border-t border-border-light/80 flex flex-wrap justify-center gap-x-8 gap-y-3 text-sm"
                >
                  <div className="flex items-center gap-2">
                    <span className="text-base">🛡️</span>
                    <span className="text-text-body font-medium">12-Year Warranty</span>
                  </div>
                  <div className="hidden sm:block h-4 w-px bg-border-light self-center" />
                  <div className="flex items-center gap-2">
                    <span className="text-base">⚡</span>
                    <span className="text-text-body font-medium">DEYE & SOLIS Inverters</span>
                  </div>
                  <div className="hidden sm:block h-4 w-px bg-border-light self-center" />
                  <div className="flex items-center gap-2">
                    <span className="text-base">🔋</span>
                    <span className="text-text-body font-medium">LiFePO₄ Battery</span>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </>
        )}
      </motion.div>
    </section>
  );
}

/* ---------------------- ABOUT: SIMPLE FULL-BLEED ----------------------- */
function AboutSimple() {
  return (
    <section id="about" className="scroll-mt-16">
      <motion.div
        className="relative w-full"
        initial={{ opacity: 0, y: 50 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: "easeOut" }}
        viewport={{ once: true, margin: "-20% 0px -20% 0px" }}
      >
        {/* full-width section background */}
        <div className="w-full bg-base text-text-body">
          <div className="py-20 md:py-28">
            {/* readable content width inside the full-bleed band */}
            <div className="mx-auto max-w-[92rem] px-5 sm:px-8 md:px-10">
              <SectionHeaderSimple eyebrow="How Solar Works" title="Sunlight to Savings" />
              <p className="mt-6 text-lg text-text-body text-center max-w-3xl mx-auto leading-relaxed">
                Solar converts sunlight into electricity through PV modules
                (DC), which an inverter turns into AC for your home or business.
                With net metering, excess daytime energy earns credits;
                optional batteries store power for night and outages.
              </p>

              <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6">
                <SimpleCard icon="💰" title="Lower Operating Costs">
                  Generate your own electricity and hedge against rate hikes.
                  Credits can offset your bill over the year.
                </SimpleCard>
                <SimpleCard icon="⚡" title="Reliable & Scalable">
                  Start small and expand. Add batteries for backup or
                  peak-shaving when it makes sense.
                </SimpleCard>
                <SimpleCard icon="🌱" title="Clean, Quiet Power">
                  No fuel, no noise, minimal maintenance — cut emissions while
                  improving onsite resilience.
                </SimpleCard>
              </div>

              <div className="mt-20 grid grid-cols-1 md:grid-cols-2 gap-12 items-start">
                <div className="space-y-6">
                  <div className="inline-flex items-center gap-2">
                    <span className="h-px w-8 bg-accent-sun" />
                    <span className="uppercase tracking-[0.18em] text-[11px] text-accent-sun font-semibold">
                      Who We Are
                    </span>
                  </div>
                  <h3 className="font-display text-3xl md:text-4xl font-bold text-text-primary tracking-tight leading-tight">
                    Engineering-first solar for{" "}
                    <span className="text-accent-sun">Filipino homes</span>
                  </h3>
                  <p className="text-text-body leading-relaxed text-[15px]">
                    <span className="font-semibold text-text-primary">SOLARES</span> designs
                    and builds high-efficiency PV systems — tidy installs, clear ROI
                    guidance, and engineering-first layouts.
                  </p>
                  <ul className="space-y-4 text-[15px] leading-relaxed text-text-body border-l-2 border-accent-sun/30 pl-5">
                    <li>
                      <span className="font-semibold text-text-primary">Services — </span>
                      survey & modeling, sizing, drawings, permitting, installation, monitoring, after-sales.
                    </li>
                    <li>
                      <span className="font-semibold text-text-primary">Approach — </span>
                      transparent proposals, strong warranties, clean cable management, inspector-ready docs.
                    </li>
                    <li>
                      <span className="font-semibold text-text-primary">Safety — </span>
                      proper conductor sizing, SPD/MCB/RCD, grounding/bonding, and clear labels.
                    </li>
                  </ul>
                </div>

                <div className="grid grid-cols-3 gap-3">
                  <KPI value="98%" label="On-time Installs" />
                  <KPI value="25–30 yr" label="Panel lifespan" />
                  <KPI value="24/7" label="Monitoring" />
                  <div className="col-span-3 p-7 rounded-2xl bg-gradient-to-br from-accent-sun/10 to-amber-50 border border-accent-sun/20">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-lg">📋</span>
                      <h4 className="font-display font-bold text-text-primary tracking-tight">
                        Typical Deliverables
                      </h4>
                    </div>
                    <ul className="text-sm space-y-2.5 text-text-body">
                      <li className="flex gap-2">
                        <span className="text-accent-sun mt-0.5">✓</span>
                        <span>One-line & layout drawings</span>
                      </li>
                      <li className="flex gap-2">
                        <span className="text-accent-sun mt-0.5">✓</span>
                        <span>String/MPPT configuration & voltage checks</span>
                      </li>
                      <li className="flex gap-2">
                        <span className="text-accent-sun mt-0.5">✓</span>
                        <span>Bill of materials & warranties</span>
                      </li>
                      <li className="flex gap-2">
                        <span className="text-accent-sun mt-0.5">✓</span>
                        <span>Owner's manual & maintenance checklist</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
            {/* /max-w for readability only */}
          </div>
        </div>
      </motion.div>
    </section>
  );
}

/* ----------------------- Presentational helpers ------------------------ */
function SectionHeaderSimple({ title, eyebrow }) {
  return (
    <div className="text-center">
      {eyebrow && (
        <div className="inline-flex items-center gap-2 mb-4">
          <span className="h-px w-8 bg-accent-sun" />
          <span className="uppercase tracking-[0.18em] text-[11px] text-accent-sun font-semibold">
            {eyebrow}
          </span>
          <span className="h-px w-8 bg-accent-sun" />
        </div>
      )}
      <h2 className="font-display text-3xl md:text-5xl font-bold text-text-primary tracking-tight leading-tight">
        {title}
      </h2>
    </div>
  );
}

function KPI({ value, label }) {
  return (
    <div className="p-5 text-center rounded-xl bg-card border border-border-light hover:border-accent-sky hover:shadow-lg hover:-translate-y-0.5 transition-all">
      <div className="font-numeric text-2xl md:text-3xl font-bold text-accent-sky">{value}</div>
      <div className="mt-2 text-[10px] uppercase tracking-[0.15em] text-text-muted font-semibold">
        {label}
      </div>
    </div>
  );
}

function SimpleCard({ icon, title, children }) {
  return (
    <div className="group p-7 rounded-2xl bg-card border border-border-light hover:border-accent-sun/40 hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
      <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-accent-sun/10 text-2xl mb-4 group-hover:bg-accent-sun/20 group-hover:scale-110 transition-all">
        {icon}
      </div>
      <h3 className="font-display font-semibold text-lg text-text-primary tracking-tight">{title}</h3>
      <p className="mt-2 text-sm text-text-body leading-relaxed">{children}</p>
    </div>
  );
}

/* ------------------- VALUE STRIP (ICON GRID) ------------------- */
const VALUE_PROPS = [
  { icon: "🛡️", label: "12-Year Warranty" },
  { icon: "⚡", label: "DEYE & SOLIS" },
  { icon: "🔋", label: "LiFePO₄ Battery" },
  { icon: "📋", label: "Full Permits" },
];

function SocialProofStrip() {
  return (
    <div className="relative w-full bg-white border-y border-border-light py-10">
      <div className="mx-auto max-w-[92rem] px-5 sm:px-8 md:px-10">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:divide-x md:divide-border-light">
          {VALUE_PROPS.map((item) => (
            <div
              key={item.label}
              className="flex items-center justify-center gap-3 md:px-4"
            >
              <span className="text-3xl">{item.icon}</span>
              <span className="text-sm font-semibold text-text-primary tracking-tight">
                {item.label}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ------------------- SOLAR CALCULATOR (NEW) ------------------- */
function SolarCalculator() {
  const [inputType, setInputType] = React.useState("bill"); // 'bill' or 'kwh'
  const [billValue, setBillValue] = React.useState(5000);
  const [kwhValue, setKwhValue] = React.useState(450);
  const [meralcoRate, setMeralcoRate] = React.useState(11);

  const kwhPerMonth =
    inputType === "bill" ? Math.round(billValue / meralcoRate) : kwhValue;

  const getRecommendedSystem = () => {
    if (kwhPerMonth < 300) return { kw: "3.6", price: 125000, tag: "3.6 kW Grid-Tie" };
    if (kwhPerMonth < 500) return { kw: "5.5", price: 160000, tag: "5.5 kW Grid-Tie" };
    if (kwhPerMonth < 800) return { kw: "8.6", price: 250000, tag: "8.6 kW Grid-Tie" };
    return { kw: "10.45", price: 310000, tag: "10.45 kW Grid-Tie" };
  };

  const recommended = getRecommendedSystem();
  const monthlyGeneration = parseFloat(recommended.kw) * 150;
  const monthlySavings = Math.min(monthlyGeneration, kwhPerMonth) * meralcoRate;
  const paybackYears = (recommended.price / (monthlySavings * 12)).toFixed(1);
  const netSavings25yr = Math.round(monthlySavings * 12 * 25 - recommended.price);

  return (
    <section id="calculator" className="scroll-mt-16">
      <motion.div
        className="relative w-full"
        initial={{ opacity: 0, y: 50 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: "easeOut" }}
        viewport={{ once: true, margin: "-20% 0px -20% 0px" }}
      >
        <div className="w-full bg-section py-20 md:py-28">
          <div className="mx-auto max-w-[92rem] px-5 sm:px-8 md:px-10">
            <SectionHeaderSimple eyebrow="Savings Calculator" title="How Much Can You Save?" />
            <p className="mt-6 text-lg text-text-body text-center mb-14 max-w-2xl mx-auto leading-relaxed">
              Enter your monthly bill or average kWh usage to see your potential savings.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12">
              {/* Input Panel */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2, duration: 0.6 }}
                viewport={{ once: true }}
                className="space-y-6"
              >
                {/* Toggle Buttons */}
                <div className="flex gap-2 bg-border-light rounded-lg p-1 w-fit">
                  <button
                    onClick={() => setInputType("bill")}
                    className={`flex-1 px-4 py-2 rounded-md font-medium transition ${
                      inputType === "bill"
                        ? "bg-accent-sun text-white"
                        : "text-text-body hover:text-text-primary"
                    }`}
                  >
                    Monthly Bill (₱)
                  </button>
                  <button
                    onClick={() => setInputType("kwh")}
                    className={`flex-1 px-4 py-2 rounded-md font-medium transition ${
                      inputType === "kwh"
                        ? "bg-accent-sun text-white"
                        : "text-text-body hover:text-text-primary"
                    }`}
                  >
                    Monthly kWh
                  </button>
                </div>

                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <label className="text-sm font-medium text-text-muted">
                      {inputType === "bill" ? "Monthly Bill" : "Monthly kWh"}
                    </label>
                    <span className="text-2xl font-bold text-accent-sky">
                      {inputType === "bill" ? `₱${billValue.toLocaleString()}` : `${kwhValue} kWh`}
                    </span>
                  </div>
                  <input
                    type="range"
                    min={inputType === "bill" ? "500" : "50"}
                    max={inputType === "bill" ? "20000" : "1800"}
                    step={inputType === "bill" ? "100" : "10"}
                    value={inputType === "bill" ? billValue : kwhValue}
                    onChange={(e) => {
                      if (inputType === "bill") setBillValue(parseInt(e.target.value));
                      else setKwhValue(parseInt(e.target.value));
                    }}
                    className="w-full h-2 bg-border-light rounded-lg appearance-none cursor-pointer accent-accent-sun"
                  />
                  <div className="flex justify-between text-xs text-text-muted">
                    <span>{inputType === "bill" ? "₱500" : "50 kWh"}</span>
                    <span>{inputType === "bill" ? "₱20,000" : "1,800 kWh"}</span>
                  </div>
                </div>

                <div className="space-y-3">
                  <label className="text-sm font-medium text-text-body">
                    Meralco Rate: ₱{meralcoRate}/kWh
                  </label>
                  <input
                    type="number"
                    min="8"
                    max="15"
                    step="0.5"
                    value={meralcoRate}
                    onChange={(e) => setMeralcoRate(parseFloat(e.target.value))}
                    className="w-full px-4 py-2.5 rounded-lg bg-card border border-border-light text-text-primary font-mono focus:outline-none focus:ring-2 focus:ring-accent-sun"
                  />
                </div>

                <button
                  onClick={() => {}}
                  className="w-full px-6 py-3.5 rounded-lg bg-accent-sun hover:bg-orange-600 text-white font-bold transition transform hover:translate-y-[-2px] hover:shadow-md hidden md:block"
                >
                  Compute Savings
                </button>
              </motion.div>

              {/* Results Panel */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3, duration: 0.6 }}
                viewport={{ once: true }}
                className="space-y-6"
              >
                {/* Recommended System Card */}
                <div className="p-6 rounded-xl bg-accent-sun-light/30 border-2 border-accent-sun shadow-md">
                  <div className="text-sm font-medium text-accent-sun uppercase tracking-widest mb-2">
                    Recommended System
                  </div>
                  <div className="text-3xl md:text-4xl font-bold text-text-primary mb-1">
                    {recommended.tag}
                  </div>
                  <div className="text-xl text-accent-sun font-sharetech font-bold">
                    ₱{recommended.price.toLocaleString()}
                  </div>
                </div>

                {/* Savings Metrics */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 rounded-lg bg-card border border-border-light">
                    <div className="text-xs font-medium text-text-muted uppercase tracking-wide mb-1">
                      Monthly Savings
                    </div>
                    <div className="text-2xl font-bold text-accent-sun">
                      ₱{monthlySavings.toLocaleString("en-PH", {
                        maximumFractionDigits: 0,
                      })}
                    </div>
                  </div>
                  <div className="p-4 rounded-lg bg-card border border-border-light">
                    <div className="text-xs font-medium text-text-muted uppercase tracking-wide mb-1">
                      Payback Period
                    </div>
                    <div className="text-2xl font-bold text-accent-sky">
                      {paybackYears} yrs
                    </div>
                  </div>
                </div>

                {/* 25-Year Savings */}
                <div className="p-4 rounded-lg bg-accent-green/10 border border-accent-green/30">
                  <div className="text-xs font-medium text-text-muted uppercase tracking-wide mb-1">
                    25-Year Net Savings
                  </div>
                  <div className="text-3xl font-bold text-accent-green">
                    ₱{netSavings25yr.toLocaleString()}
                  </div>
                </div>

                {/* CTAs */}
                <div className="space-y-3 pt-4">
                  <a
                    href="/products"
                    className="block w-full px-6 py-3 rounded-lg bg-card border border-border-light text-text-body text-center font-bold transition hover:border-accent-sun hover:text-accent-sun"
                  >
                    → View This Package
                  </a>
                  <button
                    onClick={() => document.querySelector("#contacts")?.scrollIntoView({ behavior: "smooth" })}
                    className="w-full px-6 py-3 rounded-lg bg-accent-sun hover:bg-orange-600 text-white font-bold transition"
                  >
                    → Request a Quote
                  </button>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </motion.div>
    </section>
  );
}

export default Main;
